/**
 * 상품 소싱 체크리스트 서비스
 * workflowService.js의 aiRocketRegRequest를 활용하여 Gemini API로 체크리스트 생성
 */

import workflowService from '../workflowService'

export default {
  /**
   * 상품 소싱 체크리스트 생성
   * @param {Array} topProducts - TOP3 소싱 상품 데이터
   * @param {Object} analysisData - 키워드 분석 데이터
   * @returns {Promise<Object>} 체크리스트 결과
   */
  async generateSourcingChecklist(topProducts, analysisData) {
    try {
      console.log('🔍 상품 소싱 체크리스트 생성 시작:', {
        topProductsCount: topProducts.length,
        hasAnalysisData: !!analysisData
      })

      // 프롬프트 생성
      const prompt = this.createChecklistPrompt(topProducts, analysisData)

      // 이미지 URL 수집 (TOP3 상품 이미지)
      const imageUrls = topProducts
        .filter(product => product.imagePath)
        .map(product => product.imagePath)
        .slice(0, 3) // 최대 3개 이미지

      console.log('📸 수집된 이미지 URL:', imageUrls.length)

      // workflowService를 통한 AI 요청
      const requestData = {
        workType: 'SOURCING_CHECKLIST',
        task: 'generate_checklist',
        requestArr: [{
          prompt: prompt,
          images: imageUrls
        }]
      }

      const result = await workflowService.requestExtensionWithPromise(
        requestData,
        {
          timeout: 60000, // 60초 타임아웃
          maxRetries: 2   // 최대 2회 재시도
        }
      )

      console.log('✅ 상품 소싱 체크리스트 생성 완료:', result)
      return this.parseChecklistResult(result)

    } catch (error) {
      console.error('❌ 상품 소싱 체크리스트 생성 실패:', error)
      throw error
    }
  },

  /**
   * 체크리스트 생성을 위한 프롬프트 생성
   * @param {Array} topProducts - TOP3 소싱 상품
   * @param {Object} analysisData - 분석 데이터
   * @returns {string} 최적화된 프롬프트
   */
  createChecklistPrompt(topProducts, analysisData) {
    const productInfo = topProducts.map((product, index) => `
${index + 1}위 상품:
- 상품명: ${product.productName}
- 가격: ${product.salePrice?.toLocaleString()}원
- RPI: ${parseFloat(product.overall || 0).toFixed(2)}
- CTR: ${parseFloat(product.ctr || 0).toFixed(2)}%
- CVR: ${parseFloat(product.cvr || 0).toFixed(2)}%
- 브랜드: ${product.brandName || '정보없음'}
- 판매량: ${product.salesLast28d || 0}개
    `).join('\n')

    const marketInfo = analysisData ? `
시장 분석 정보:
- 총 상품 수: ${analysisData.totalProducts || 0}개
- 평균 가격: ${analysisData.avgSalePrice?.toLocaleString() || 0}원
- 중간값 CTR: ${analysisData.medianCTR || 0}%
- 중간값 CVR: ${analysisData.medianCVR || 0}%
- 최적 카테고리: ${analysisData.optimizedCategory || '정보없음'}
    ` : ''

    return `당신은 전문 상품 소싱 컨설턴트입니다. 제공된 TOP3 고성과 상품 데이터와 이미지를 분석하여 실용적인 상품 소싱 체크리스트를 생성해주세요.

${productInfo}
${marketInfo}

다음 JSON 형식으로 체크리스트를 생성해주세요:

{
  "checklist": {
    "productAnalysis": [
      {
        "category": "상품 품질",
        "items": [
          {
            "title": "체크 항목명",
            "description": "구체적인 확인 방법",
            "priority": "high|medium|low",
            "benchmark": "기준값 또는 목표"
          }
        ]
      }
    ],
    "marketAnalysis": [
      {
        "category": "시장 분석",
        "items": [
          {
            "title": "체크 항목명",
            "description": "구체적인 확인 방법",
            "priority": "high|medium|low",
            "benchmark": "기준값 또는 목표"
          }
        ]
      }
    ],
    "competitorAnalysis": [
      {
        "category": "경쟁사 분석",
        "items": [
          {
            "title": "체크 항목명",
            "description": "구체적인 확인 방법",
            "priority": "high|medium|low",
            "benchmark": "기준값 또는 목표"
          }
        ]
      }
    ],
    "riskAssessment": [
      {
        "category": "리스크 평가",
        "items": [
          {
            "title": "체크 항목명",
            "description": "구체적인 확인 방법",
            "priority": "high|medium|low",
            "benchmark": "기준값 또는 목표"
          }
        ]
      }
    ]
  },
  "summary": {
    "totalItems": 0,
    "highPriorityItems": 0,
    "keyInsights": ["핵심 인사이트 1", "핵심 인사이트 2"],
    "recommendations": ["추천사항 1", "추천사항 2"]
  }
}

체크리스트 생성 시 고려사항:
1. 제공된 TOP3 상품의 성공 요인을 분석하여 실용적인 체크 항목 생성
2. 각 항목은 구체적이고 실행 가능해야 함
3. 우선순위(high/medium/low)를 명확히 구분
4. 정량적 기준값이나 목표를 제시
5. 상품 이미지에서 확인할 수 있는 시각적 요소도 포함
6. 총 15-25개의 체크 항목으로 구성 (카테고리별 3-8개)

반드시 유효한 JSON 형식으로만 응답해주세요.`
  },

  /**
   * AI 응답 결과 파싱
   * @param {Object} result - AI 응답 결과
   * @returns {Object} 파싱된 체크리스트 데이터
   */
  parseChecklistResult(result) {
    try {
      // result가 이미 객체인 경우
      if (typeof result === 'object' && result.checklist) {
        return result
      }

      // result가 문자열인 경우 JSON 파싱
      if (typeof result === 'string') {
        const parsed = JSON.parse(result)
        if (parsed.checklist) {
          return parsed
        }
      }

      // 기본 구조 반환
      return this.getDefaultChecklist()

    } catch (error) {
      console.error('체크리스트 결과 파싱 실패:', error)
      return this.getDefaultChecklist()
    }
  },

  /**
   * 기본 체크리스트 구조 반환
   * @returns {Object} 기본 체크리스트
   */
  getDefaultChecklist() {
    return {
      checklist: {
        productAnalysis: [{
          category: "상품 품질",
          items: [{
            title: "상품 이미지 품질 확인",
            description: "고해상도 이미지, 다양한 각도 촬영 여부 확인",
            priority: "high",
            benchmark: "최소 5장 이상, 1000px 이상 해상도"
          }]
        }],
        marketAnalysis: [{
          category: "시장 분석",
          items: [{
            title: "가격 경쟁력 분석",
            description: "유사 상품 대비 가격 경쟁력 확인",
            priority: "high",
            benchmark: "시장 평균 대비 ±20% 범위"
          }]
        }],
        competitorAnalysis: [{
          category: "경쟁사 분석",
          items: [{
            title: "리뷰 분석",
            description: "경쟁 상품의 고객 리뷰 및 평점 분석",
            priority: "medium",
            benchmark: "평점 4.0 이상, 리뷰 100개 이상"
          }]
        }],
        riskAssessment: [{
          category: "리스크 평가",
          items: [{
            title: "공급업체 신뢰도",
            description: "공급업체의 배송, 품질, 서비스 신뢰도 확인",
            priority: "high",
            benchmark: "업체 평점 4.5 이상, 운영 기간 1년 이상"
          }]
        }]
      },
      summary: {
        totalItems: 4,
        highPriorityItems: 3,
        keyInsights: ["상품 이미지와 가격이 핵심 성공 요인", "공급업체 신뢰도가 중요"],
        recommendations: ["고품질 이미지 확보", "경쟁력 있는 가격 설정"]
      }
    }
  }
}
